import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { AlertTriangle, AlertCircle, CheckCircle, Eye, Clock, Trash2, Check } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { getBackendUrl } from '../../utils/api';

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

export const MasterAIErrorLogsContent = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedError, setSelectedError] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filterResolved, setFilterResolved] = useState(null);
  const [filterRestaurant, setFilterRestaurant] = useState('');
  
  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [page, filterResolved, filterRestaurant]);
  
  const fetchLogs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      let url = `${API}/master/ai-error-logs?page=${page}&limit=20`;
      
      if (filterResolved !== null) {
        url += `&resolved=${filterResolved}`;
      }
      if (filterRestaurant) {
        url += `&restaurant_id=${filterRestaurant}`;
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setLogs(response.data.logs);
      setTotalPages(response.data.pages);
      setStats(response.data.stats);
    } catch (error) {
      console.error('Erro ao buscar logs:', error);
      toast.error('Erro ao carregar logs de erros');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/master/ai-error-stats?days=7`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(prevStats => ({ ...prevStats, ...response.data }));
    } catch (error) {
      console.error('Erro ao buscar estatísticas:', error);
    }
  };
  
  const handleResolve = async (errorId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/master/ai-error-logs/${errorId}/resolve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Erro marcado como resolvido');
      fetchLogs();
    } catch (error) {
      console.error('Erro ao resolver:', error);
      toast.error('Erro ao marcar como resolvido');
    }
  };
  
  const handleDelete = async (errorId) => {
    if (!window.confirm('Tem certeza que deseja excluir este log?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/master/ai-error-logs/${errorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Log excluído');
      fetchLogs();
    } catch (error) {
      console.error('Erro ao excluir:', error);
      toast.error('Erro ao excluir log');
    }
  };
  
  const handleClearResolved = async () => {
    if (!window.confirm('Tem certeza que deseja excluir TODOS os erros resolvidos?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API}/master/ai-error-logs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchLogs();
    } catch (error) {
      console.error('Erro ao limpar:', error);
      toast.error('Erro ao limpar logs');
    }
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('pt-BR');
  };
  
  const viewDetail = (error) => {
    setSelectedError(error);
    setShowDetailModal(true);
  };

  return (
    <div className="space-y-6">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <AlertTriangle className="w-7 h-7 text-red-400" />
            Logs de Erros da IA
          </h2>
          <p className="text-gray-400 mt-1">Monitore e resolva erros do sistema de IA</p>
        </div>
        <button
          onClick={handleClearResolved}
          className="px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition flex items-center gap-2"
        >
          <Trash2 className="w-4 h-4" />
          Limpar Resolvidos
        </button>
      </div>
      
      {/* Cards de Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700/50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-500/20 rounded-lg">
              <AlertCircle className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Total de Erros</p>
              <p className="text-2xl font-bold text-white">{stats?.total_errors || 0}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700/50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-500/20 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-yellow-400" />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Não Resolvidos</p>
              <p className="text-2xl font-bold text-yellow-400">{stats?.unresolved || stats?.unresolved_errors || 0}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700/50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <Clock className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Hoje</p>
              <p className="text-2xl font-bold text-blue-400">{stats?.today || 0}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700/50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-500/20 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Taxa de Resolução</p>
              <p className="text-2xl font-bold text-green-400">
                {stats?.total_errors > 0 
                  ? Math.round(((stats.total_errors - (stats.unresolved || 0)) / stats.total_errors) * 100)
                  : 100}%
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Filtros */}
      <div className="flex gap-4 items-center bg-gray-800/30 p-4 rounded-xl">
        <div className="flex items-center gap-2">
          <span className="text-gray-400 text-sm">Status:</span>
          <select
            value={filterResolved === null ? 'all' : filterResolved.toString()}
            onChange={(e) => {
              const val = e.target.value;
              setFilterResolved(val === 'all' ? null : val === 'true');
              setPage(1);
            }}
            className="bg-gray-700 text-white rounded-lg px-3 py-2 text-sm"
          >
            <option value="all">Todos</option>
            <option value="false">Não Resolvidos</option>
            <option value="true">Resolvidos</option>
          </select>
        </div>
        
        <button
          onClick={() => { setPage(1); fetchLogs(); }}
          className="px-4 py-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition"
        >
          Atualizar
        </button>
      </div>
      
      {/* Lista de Erros */}
      <div className="bg-gray-800/50 rounded-xl border border-gray-700/50 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-400">
            Carregando...
          </div>
        ) : logs.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-400" />
            <p>Nenhum erro encontrado!</p>
            <p className="text-sm mt-1">A IA está funcionando perfeitamente.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-700/50">
                <tr>
                  <th className="px-4 py-3 text-left text-gray-300 text-sm font-medium">Data/Hora</th>
                  <th className="px-4 py-3 text-left text-gray-300 text-sm font-medium">Restaurante</th>
                  <th className="px-4 py-3 text-left text-gray-300 text-sm font-medium">Tipo de Erro</th>
                  <th className="px-4 py-3 text-left text-gray-300 text-sm font-medium">Mensagem</th>
                  <th className="px-4 py-3 text-left text-gray-300 text-sm font-medium">Status</th>
                  <th className="px-4 py-3 text-right text-gray-300 text-sm font-medium">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700/50">
                {logs.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-700/30 transition">
                    <td className="px-4 py-3 text-gray-300 text-sm whitespace-nowrap">
                      {formatDate(log.created_at)}
                    </td>
                    <td className="px-4 py-3 text-white text-sm">
                      {log.restaurant_name || 'Desconhecido'}
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 bg-red-500/20 text-red-400 rounded text-xs font-mono">
                        {log.error_type || 'Unknown'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-400 text-sm max-w-xs truncate">
                      {log.error_message?.substring(0, 50)}...
                    </td>
                    <td className="px-4 py-3">
                      {log.resolved ? (
                        <span className="flex items-center gap-1 text-green-400 text-sm">
                          <CheckCircle className="w-4 h-4" />
                          Resolvido
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-yellow-400 text-sm">
                          <AlertTriangle className="w-4 h-4" />
                          Pendente
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => viewDetail(log)}
                          className="p-2 text-blue-400 hover:bg-blue-500/20 rounded-lg transition"
                          title="Ver detalhes"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {!log.resolved && (
                          <button
                            onClick={() => handleResolve(log.id)}
                            className="p-2 text-green-400 hover:bg-green-500/20 rounded-lg transition"
                            title="Marcar como resolvido"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(log.id)}
                          className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition"
                          title="Excluir"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {/* Paginação */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 p-4 border-t border-gray-700/50">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1 bg-gray-700 text-white rounded disabled:opacity-50"
            >
              Anterior
            </button>
            <span className="text-gray-400">
              Página {page} de {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-3 py-1 bg-gray-700 text-white rounded disabled:opacity-50"
            >
              Próxima
            </button>
          </div>
        )}
      </div>
      
      {/* Modal de Detalhes */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-400" />
              Detalhes do Erro
            </DialogTitle>
          </DialogHeader>
          
          {selectedError && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-gray-400 text-sm">Data/Hora</p>
                  <p className="text-white">{formatDate(selectedError.created_at)}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Restaurante</p>
                  <p className="text-white">{selectedError.restaurant_name}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Telefone do Cliente</p>
                  <p className="text-white font-mono">{selectedError.customer_phone}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Tipo de Erro</p>
                  <span className="px-2 py-1 bg-red-500/20 text-red-400 rounded text-sm font-mono">
                    {selectedError.error_type}
                  </span>
                </div>
              </div>
              
              <div>
                <p className="text-gray-400 text-sm mb-1">Mensagem do Cliente</p>
                <div className="bg-gray-700/50 p-3 rounded-lg">
                  <p className="text-white text-sm">{selectedError.customer_message || 'N/A'}</p>
                </div>
              </div>
              
              <div>
                <p className="text-gray-400 text-sm mb-1">Mensagem de Erro</p>
                <div className="bg-red-500/10 border border-red-500/30 p-3 rounded-lg">
                  <p className="text-red-400 text-sm font-mono">{selectedError.error_message}</p>
                </div>
              </div>
              
              <div>
                <p className="text-gray-400 text-sm mb-1">Traceback</p>
                <div className="bg-gray-900 p-3 rounded-lg max-h-48 overflow-y-auto">
                  <pre className="text-gray-300 text-xs font-mono whitespace-pre-wrap">
                    {selectedError.traceback || 'N/A'}
                  </pre>
                </div>
              </div>
              
              {selectedError.resolved && (
                <div className="bg-green-500/10 border border-green-500/30 p-3 rounded-lg">
                  <p className="text-green-400 text-sm flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Resolvido em {formatDate(selectedError.resolved_at)} por {selectedError.resolved_by}
                  </p>
                  {selectedError.notes && (
                    <p className="text-gray-300 text-sm mt-2">Notas: {selectedError.notes}</p>
                  )}
                </div>
              )}
              
              {!selectedError.resolved && (
                <button
                  onClick={() => {
                    handleResolve(selectedError.id);
                    setShowDetailModal(false);
                  }}
                  className="w-full py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition flex items-center justify-center gap-2"
                >
                  <Check className="w-4 h-4" />
                  Marcar como Resolvido
                </button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

